
import { ElMessage } from 'element-plus'
import { withEnterpriseId, getEnterpriseIdFromUrl } from '@/utils/enterprise-context'

const PREVIEWABLE_EXTENSIONS = ['webp', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xlsx', 'xls', 'pdf']
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xlsx', 'xls']

/**
 * Open a file URL for preview.
 * Office files are opened via Microsoft Office Online; images and PDFs open directly.
 * @param url      Direct file URL (previewUrl takes precedence if both are passed)
 * @param filename Used to infer the file extension when the URL contains query params
 * @param warnings Override the default Japanese warning messages shown via ElMessage
 */
export function previewFile(
  url: string | undefined | null,
  filename?: string,
  warnings?: { urlNotFound?: string; notSupported?: string }
): void {
  if (!url) {
    ElMessage.warning(warnings?.urlNotFound ?? 'ファイルURLが見つかりません')
    return
  }

  const cleanName = filename || url.split('?')[0]
  const ext = cleanName.split('.').pop()?.toLowerCase() ?? ''

  // if (!PREVIEWABLE_EXTENSIONS.includes(ext)) {
  //   ElMessage.warning(warnings?.notSupported ?? 'このファイル形式はプレビューに対応していません')
  //   return
  // }

  if (OFFICE_EXTENSIONS.includes(ext)) {
    //const viewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`
    window.open(withEnterpriseIdIfInternal(url), '_blank', 'noopener,noreferrer')
    return
  }

  window.open(withEnterpriseIdIfInternal(url), '_blank', 'noopener,noreferrer')
}

export function requestAnimationDebounce<T extends Function>(fn: T) {
  let timer: number = -1
  return function (this: any, ...args: any) {
    cancelAnimationFrame(timer)
    timer = requestAnimationFrame(() => {
      fn.apply(this, args)
    })
  }
}

export const isEllipsisHeight = (dom: any, maxWidth: any, parentNode: any) => {
  const checkDom = dom.cloneNode();
  const computedStyle = window.getComputedStyle(dom);
  let parent, flag;
  checkDom.style.cssText = `
  diaplay: block;
  max-width: initial;
  width: ${dom.offsetWidth}px;
  height: auto;
  overflow: unset;
  position: absolute;
  top: 0;
  zIndex: -99;
  opacity: 0;
  whiteSpace: nowrap;
  font-family: ${computedStyle.fontFamily};
  font-size: ${computedStyle.fontSize};
  display: block;
  `;

  checkDom.innerHTML = dom.innerHTML;

  parent = document.body;

  parent.appendChild(checkDom);
  const autoHeight = checkDom.getBoundingClientRect().height;
  const realHeight = maxWidth || dom.getBoundingClientRect().height;
  flag = autoHeight > realHeight;
  parent.removeChild(checkDom);
  return flag;
};
/**
 * 内容判断是否超出容器
 */
export function isEllipsis(dom: HTMLElement, maxWidth?: number) {
  const computedStyle = window.getComputedStyle(dom)

  // 检测是否使用了多行省略 (-webkit-line-clamp)
  const lineClamp = computedStyle.webkitLineClamp || computedStyle.getPropertyValue('-webkit-line-clamp')
  const boxOrient = computedStyle.webkitBoxOrient || computedStyle.getPropertyValue('-webkit-box-orient')
  const isMultiLineEllipsis = lineClamp && lineClamp !== 'none' && boxOrient === 'vertical'

  if (isMultiLineEllipsis) {
    // 多行省略：比较 scrollHeight 和 clientHeight
    return dom.scrollHeight > dom.clientHeight
  }

  // 单行省略：原有逻辑，比较宽度
  const checkDom = dom.cloneNode() as HTMLElement
  let parent, flag
  // display: ${computedStyle.display}; 避免 影响flex布局
  checkDom.style.cssText = `
    display: ${computedStyle.display};
    max-width: initial;
    width: auto;
    height: ${dom.offsetHeight}px;
    overflow: unset;
    position: absolute;
    zIndex: -1;
    opacity: 0;
    whiteSpace: nowrap;
    font-family: ${computedStyle.fontFamily};
    font-size: ${computedStyle.fontSize}
    `
  checkDom.classList.add('overtip-copy-dom')

  checkDom.innerHTML = dom.innerHTML

  parent = document.body

  parent.appendChild(checkDom)
  const autoWidth = checkDom.getBoundingClientRect().width
  const realWidth = maxWidth || dom.getBoundingClientRect().width
  flag = autoWidth > realWidth
  parent.removeChild(checkDom)
  return flag
}

export type DebounceHandler<T> = {
  (...h: any): T
  clear: () => void
}
export function debounce<T>(wait: number, fn: (...h: any) => T) {
  let timer: any

  const handleFn = function (this: any, ...args: any) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, wait);
  }
  handleFn.clear = () => {
    clearTimeout(timer)
    return handleFn
  }
  return handleFn
}

export function throttle(time: number, handleFn: (...h: any) => any) {
  let starTime = -Infinity
  const dd = debounce(time, handleFn)
  return function tt(this: any, ...arg: any) {
    const nowTime = performance.now()
    if (nowTime - starTime >= time) {
      handleFn.call(this, ...arg)
      starTime = nowTime
    } else {
      dd.call(this, ...arg)
    }
  }
}
export function isPromise<T>(val: unknown): val is Promise<T> {
  return Object.prototype.toString.call(val) === '[object Promise]'
}
export type RafInterval = {
  (): void
  clear: () => void
  start: (h?: any) => void
}
export function getRaf(cb: (h?: any) => any, time = 0): RafInterval {
  let rafId: number | null = null
  let keep = true
  let startTime = -Infinity
  const thresholdTime = time
  async function rafFn(this: any, ...arg: any) {
    let currentTime = performance.now()
    if (currentTime - startTime < thresholdTime) {
      cancelAnimationFrame(rafId as number)
      rafId = requestAnimationFrame(rafFn.bind(this, ...arg))
      return
    }
    startTime = performance.now()
    try {
      const cbRes = cb.apply(this, arg)
      if (isPromise(cbRes)) {
        await cbRes
      }
    } catch (err) {
      console.error(err)
    }
    if (!keep) return
    rafId = requestAnimationFrame(rafFn.bind(this, ...arg))
  }
  rafFn.clear = () => {
    keep = false
    if (rafId) cancelAnimationFrame(rafId)
  }
  rafFn.start = (h?: any) => {
    rafFn.clear()
    keep = true
    rafFn(h)
  }
  return rafFn
}

/**
 * @description: 处理跟随tip或者弹窗，滚动时抖动问题
 * @return {*}
 */
export function addScrollRefresh(fn: (e: Event) => void): () => void {
  const raf = getRaf(fn)
  let isStart = false
  const scrollStart = (e: Event) => {
    if (!isStart) {
      isStart = true
      raf.start(e)
    }
  }
  const scrollStop = debounce(320, () => {
    raf.clear()
    isStart = false
  })
  const scrollCb = (e: Event) => {
    scrollStart(e)
    scrollStop(e)
  }
  window.addEventListener('resize', scrollCb)
  window.addEventListener('scroll', scrollCb, { capture: true })
  return () => {
    window.removeEventListener('scroll', scrollCb, { capture: true })
    window.removeEventListener('resize', scrollCb)
    raf.clear()
  }
}

/**
 * 格式化营业时间显示
 * @param startTime 开始时间（HH:mm 格式，如 "08:55"）
 * @param endTime 结束时间（HH:mm 格式，超过24小时表示次日，如 "25:00"）
 * @param nextDayText 翌日文案（国际化文本，如"翌日"或"次日"）
 * @returns 格式化后的营业时间字符串，如 "08:55 ~ 23:00" 或 "08:55 ~ 翌日 01:00"
 */
export function formatBusinessHours(
  startTime: string | undefined | null,
  endTime: string | undefined | null,
  nextDayText: string
): string {
  if (!startTime || !endTime) {
    return '-';
  }

  const timePattern = /^(\d{1,2}):(\d{2})$/;
  const endMatch = endTime.match(timePattern);

  if (!endMatch) {
    return '-';
  }

  const endHours = parseInt(endMatch[1], 10);
  const endMinutes = endMatch[2];

  if (endHours >= 24) {
    const displayHours = String(endHours - 24).padStart(2, '0');
    return `${startTime} ~ ${nextDayText} ${displayHours}:${endMinutes}`;
  }

  return `${startTime} ~ ${endTime}`;
}

/**
 * 格式化邮政编码显示，形如 xxx-xxxx
 * @param postalCode 原始邮政编码（可能包含非数字字符）
 * @returns 格式化后的邮政编码，无值时返回 '-'
 */
export function formatPostalCode(postalCode: string | number | undefined | null): string {
  if (postalCode === undefined || postalCode === null || postalCode === '') {
    return '-';
  }
  const digits = String(postalCode).replace(/[^0-9]/g, '');
  if (!digits) {
    return '-';
  }
  if (digits.length <= 3) {
    return digits;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
}

/**
 * 将 ISO datetime 格式转换为时间字符串 (HH:mm)
 * @param isoString ISO datetime 字符串
 * @param startIsoString 开始时间的 ISO 字符串（用于判断是否跨天）
 * @returns 时间字符串 (HH:mm 格式)
 */
export const parseISOToTime = (isoString: string, startIsoString?: string) => {
  if (!isoString) {
    return '';
  }
  // 如果已经是 HH:mm 格式，直接返回
  const timePattern = /^\d{1,2}:\d{2}$/;
  if (timePattern.test(isoString)) {
    return isoString;
  }

  // 从 ISO 字符串中提取时间部分
  const date = new Date(isoString);
  let hours = date.getHours();

  // 如果提供了 startIsoString，检查是否跨天
  if (startIsoString) {
    const startDate = new Date(startIsoString);
    const endDate = date;

    // 比较日期部分（忽略时间）
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    // 如果结束日期比开始日期大一天，小时数 +24
    const dayDiff = Math.round((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
    if (dayDiff === 1) {
      hours += 24;
    }
  }

  const hoursStr = String(hours).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${hoursStr}:${minutes}`;
};

/** 是否是本站地址（router.resolve 出来的相对路径，或同源的绝对地址） */
function isInternalUrl(url: string): boolean {
  if (!url) {
    return false
  }
  // 注意排除 //example.com 这种协议相对的外部地址
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true
  }
  try {
    return new URL(url).origin === window.location.origin
  } catch {
    return false
  }
}

/**
 * 站内地址补上当前企业的 company_id；外部地址（附件、在线预览、短链等）原样返回
 *
 * 新标签页/新窗口打开时，路由守卫补 company_id 的逻辑不生效——新页面是一次全新的
 * bootstrap，地址栏没有这个参数就会落到服务端的默认企业，而不是用户当前正在看的企业。
 * 统一在这里兜底，免得每个调用点各写一遍、又总有遗漏。
 */
export function withEnterpriseIdIfInternal<T extends string | undefined | null>(url: T): T {
  if (!url) {
    // 保持原值（undefined/null/空串），交给调用方处理，不要凭空造出一个地址
    return url
  }
  return (isInternalUrl(url) ? withEnterpriseId(url, getEnterpriseIdFromUrl()) : url) as T
}

/**
 * 在新标签页打开 URL，兼容 Safari（Safari 会把带 windowFeatures 的 window.open 当弹窗处理）。
 * 通过模拟点击 <a target="_blank"> 让所有浏览器统一开新标签页。
 */
export function openInNewTab(url: string): void {
  const a = document.createElement('a')
  a.href = withEnterpriseIdIfInternal(url)
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
